<template>
    <form :class="cn('flex flex-col gap-2', formClass)" novalidate method="post" @submit.prevent="submit">
        <slot :is-busy :is-valid />
        <div class="flex gap-4">
            <Button variant="primary" fill="solid" size="lg" type="submit" :disabled="isBusy || !isValid">
                <span>{{ submitText }}</span>
            </Button>
            <Button v-if="formTouched" variant="default" size="lg" @click="resetForm">
                <span>Reset Form</span>
            </Button>
        </div>
    </form>
</template>
<script lang="ts">
import { safeParse, getDotPath, looseObject, type ObjectSchema, type ObjectEntries } from "valibot";
import type { HTMLAttributes, InjectionKey } from "vue";

interface FormProps {
    schema?: ObjectSchema<ObjectEntries, undefined>;
    formValues: object;
    isBusy: boolean;
    submitText?: string;
    class?: HTMLAttributes["class"];
}

interface FormEmits {
    submit: [values: object];
    reset: [];
}

interface FormContext {
    issues: ComputedRef<Record<string, string>>;
    touched: Ref<Record<string, boolean>>;
}

export const FormInjectKey: InjectionKey<FormContext> = Symbol("Form");
</script>
<script setup lang="ts">
const {
    class: formClass = "",
    schema = looseObject({}),
    isBusy,
    formValues,
    submitText = "Submit",
} = defineProps<FormProps>();
const emit = defineEmits<FormEmits>();
const touched = ref<Record<string, boolean>>({});

const formTouched = computed(() => Object.keys(touched.value).length > 0);

const parseResult = computed(() => safeParse(schema, formValues));

const isValid = computed(() => parseResult.value.success);

const issues = computed(() => {
    const output: Record<string, string> = {};
    parseResult.value.issues?.forEach((issue) => {
        const field = getDotPath(issue) ?? "";
        if (touched.value[field] === true) output[field] = issue.message;
    });

    return output;
});

function submit() {
    if (isValid.value && !isBusy) emit("submit", parseResult.value.output as object);
}

function resetForm() {
    touched.value = {};
    emit("reset");
}

provide(FormInjectKey, { issues, touched });
</script>
